import {MessageStackService,} from '@services/index';
import {MessageLevel,} from '@models/index';
import {Injectable,} from '@angular/core';
import {StringUtil, SystemConstant, Logger,} from '@services/index';
import * as moment from 'moment';

/**
 * 日付ユーティリティー
 */
@Injectable({
  providedIn: 'root',
})
export class DateUtil {

  constructor(
    private constant: SystemConstant,
    private stringUtil: StringUtil,
    private logger: Logger
  ) {}

  /***
   * [一般開発者利用可]
   * 現在日付取得
   */
  getCurrentDate(): Date {
    return new Date();
  }

    /***
   * [一般開発者利用可]
   * 和暦年→西暦年変換(平成30年→2018)
   * @wareki 和暦
   * @year 年
   */
  convertWarekiYearToSeirekiYear(wareki: '明治' | '大正' | '昭和' | '平成' | '令和', year:number): string {


    if (year < 1) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M037E',});
      throw messageStack;
    }

    let seirekiYear = 0;

    if ((wareki === '令和') && (year > 0) )
    {
      seirekiYear = year + 2018;
    }
    else if  ((wareki === '平成') && (year > 0))
    {
      seirekiYear = year + 1988;
    }
    else if ((wareki === '昭和') && (year > 0) && (year <= 64))
    {
      seirekiYear = year + 1925;
    }
    else if ((wareki === '大正') && (year > 0) && (year <= 15))
    {
      seirekiYear = year + 1911;
    }
    else if ((wareki === '明治') && (year > 0) && (year <= 45))
    {
      seirekiYear = year + 1867;
    }
      return seirekiYear.toString();
    }

  /**
   * [一般開発者利用可]
   * 日付値を指定された形式に変換します
   * @param dateString 日付値が入力されます
   * @param inputDateFormat 入力日をフォーマットする「YYYYMMDD,　YYYY-MM-DD, YYYY/MM/DD, YYYYMM, YYYY-MM, YYYY/MM」
   * @param outputDateFormat 出力日をフォーマットします「YYYYMMDD,　YYYY-MM-DD, YYYY/MM/DD, YYYY年MM月DD日
   *     YYMMDD, YY-MM-DD, YY/MM/DD, YY年MM月DD日, YYYYMM, YYYY-MM, YYYY/MM, YYYY年MM月」
   * @returns　日付は「inputDateFormat」から「outputDateFormat」にフォーマットされます
   */
  convertDateString(dateString: string | moment.Moment,
      inputDateFormat: 'YYYYMMDD' | 'YYYY-MM-DD' | 'YYYY/MM/DD' | 'YYYYMM' | 'YYYY-MM' | 'YYYY/MM',
      outputDateFormat: 'YYYYMMDD' | 'YYYY-MM-DD' | 'YYYY/MM/DD' | 'YYYY年MM月DD日'
          | 'YYMMDD' | 'YY-MM-DD' | 'YY/MM/DD' | 'YY年MM月DD日'
          | 'YYYYMM' | 'YYYY-MM' | 'YYYY/MM' | 'YYYY年MM月'): string {

    // Moment形の場合Stringに変更
    if (moment.isMoment(dateString)) {
      dateString = moment(dateString).format(inputDateFormat);
    }

    // 空のデータを確認します
    if (this.stringUtil.isEmpty(dateString)) {
      this.logger.debug('データが無効です');
      return dateString;
    }

    // 無効な形式を無視する
    if (!this.skipDataInvalid(inputDateFormat, outputDateFormat)) {
      return dateString;
    }

    // 日付を変換
    switch (outputDateFormat) {
      case this.constant.DATE_FORMAT_DATA_YYYYMMDD:
        return this.convertDate(dateString, false);

      case this.constant.DATE_FORMAT_DISPLAY_YYYYMMDD:
        return this.convertDate(dateString, false, SystemConstant.HYPHEN);

      case SystemConstant.DATE_FORMAT_YYYY_MM_DD_SLASH:
        return this.convertDate(dateString, false, SystemConstant.SLASH);

      case SystemConstant.DATE_FORMAT_YYYY_MM_DD_JP:
        return this.convertDate(dateString, true);

      case SystemConstant.DATE_FORMAT_YYMMDD:
        return this.convertDateSlice(dateString, false, 2, 8);

      case SystemConstant.DATE_FORMAT_YYMMDD_HYPHEN:
        return this.convertDateSlice(dateString, false, 2, 10, SystemConstant.HYPHEN);

      case SystemConstant.DATE_FORMAT_YYMMDD_SLASH:
        return this.convertDateSlice(dateString, false, 2, 10, SystemConstant.SLASH);

      case SystemConstant.DATE_FORMAT_YYMMDD_JP:
        return this.convertDateSlice(dateString, true, 2, 11);

      case this.constant.DATE_FORMAT_DATA_YYYYMM:
        if (dateString.length < 8) {
          return this.convertDate(dateString, false);
        } else {
          return this.convertDateSlice(dateString, false, 0, 6);
        }

      case this.constant.DATE_FORMAT_DISPLAY_YYYYMM:
        if (dateString.length < 8) {
          return this.convertDate(dateString, false, SystemConstant.HYPHEN);
        } else {
          return this.convertDateSlice(dateString, false, 0, 7, SystemConstant.HYPHEN);
        }

      case SystemConstant.DATE_FORMAT_YYYY_MM_SLASH:
        if (dateString.length < 8) {
          return this.convertDate(dateString, false, SystemConstant.SLASH);
        } else {
          return this.convertDateSlice(dateString, false, 0, 7, SystemConstant.SLASH);
        }

      case SystemConstant.DATE_FORMAT_YYYY_MM_JP:
        if (dateString.length < 8) {
          return this.convertDate(dateString, true);
        } else {
          return this.convertDateSlice(dateString, true, 0, 8);
        }
    }
  }

  /**
   * [一般開発者利用可]
   * 日付範囲チェック
   * VBのDateTimePicker型を移行する際に利用する旨
   * [依頼/QA]AP基盤 #18418 のチケット番号
   * @param dateCheck  対象日付(Date型 | string型)
   */
  checkInDateRange(dateCheck: Date | string): void {
    const checkDate = moment(dateCheck);

    // [依頼/QA]AP基盤 #29187：日付範囲をチェックする前にVBで許可されている最小値のチェックを行う。
    // 対象日付が01/01/0001 00:00:00の場合、例外を発生させないために処理を終了する。
    const dateMinValue = moment(SystemConstant.DATE_MIN_VALUE, SystemConstant.DATE_FORMAT_YYYYMMDDHHMMSS);
    if (checkDate.year() === dateMinValue.year() && checkDate.month() === dateMinValue.month()
      && checkDate.date() === dateMinValue.date() && checkDate.hour() === dateMinValue.hour()
      && checkDate.minute() === dateMinValue.minute() && checkDate.second() === dateMinValue.second()) {
      return;
    }

    const startDate = moment('1753/01/01', 'YYYY/MM/DD');
    const endDate = moment('9998/12/31', 'YYYY/MM/DD');
    const isValid = checkDate.isBetween(startDate, endDate, void 0, '[]');
    if (isValid) {
      this.logger.debug('日付範囲チェック：OK');
    } else {
      this.logger.debug('日付範囲チェック：NG');
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS02999M036E',});
      throw messageStack;
    }
  }

  /**
   * [一般開発者利用不可]
   * 有効な日付dateStringを確認してください。
   * @param dateString 日付値が入力されます
   * @returns データが無効な場合は空(True: YYYYMMDD or YYYYMM)
   */
  private stringValid(dateString: string) : string {
    const tmpDate: string = dateString.replaceAll(SystemConstant.HYPHEN, SystemConstant.EMPTY).replaceAll(SystemConstant.SLASH, SystemConstant.EMPTY);
    if ((tmpDate.length === 8 && moment(tmpDate, this.constant.DATE_FORMAT_DATA_YYYYMMDD, true).isValid())
        || (tmpDate.length === 6 && moment(tmpDate, this.constant.DATE_FORMAT_DATA_YYYYMM, true).isValid())) {
      return tmpDate;
    }
    return SystemConstant.EMPTY;
  }

  /**
   * [一般開発者利用不可]
   * 日付はフォーマットされています
   * @param dateString 日付値が入力されます
   * @param prefix 文字をフォーマットする
   * @returns 日付はフォーマットされています
   */
  private addPrefixDate(dateString: string, prefix?: string) : string {
    if (dateString.length < 8) {
      return [dateString.slice(0, 4), prefix, dateString.slice(4, 6),].join(SystemConstant.EMPTY);
    } else {
      return [dateString.slice(0, 4), prefix, dateString.slice(4, 6), prefix, dateString.slice(6, 8),].join(SystemConstant.EMPTY);
    }
  }

  /**
   * [一般開発者利用不可]
   * 日付はJP形式でフォーマットされています
   * @param dateString 日付値が入力されます
   * @returns 日付はJP形式でフォーマットされています
   */
  private addPrefixDateJP(dateString: string) : string {
    if (dateString.length < 8) {
      return [dateString.slice(0, 4), SystemConstant.YEAR_JP, dateString.slice(4, 6), SystemConstant.MONTH_JP,].join(SystemConstant.EMPTY);
    } else {
      return [dateString.slice(0, 4), SystemConstant.YEAR_JP, dateString.slice(4, 6), SystemConstant.MONTH_JP, dateString.slice(6, 8), SystemConstant.DAY_JP,].join(SystemConstant.EMPTY);
    }
  }

  /**
   * [一般開発者利用不可]
   * 日付を変換
   * @param dateString 日付値が入力されます
   * @param isJP JPの日付形式
   * @param prefix 文字をフォーマットする
   * @returns 日付を変換
   */
  private convertDate(dateString: string, isJP: boolean, prefix?: string) :string {
    const replDate = this.stringValid(dateString);
    if (SystemConstant.EMPTY === replDate) {
      return dateString;
    }

    if (isJP) {
      return this.addPrefixDateJP(replDate);
    } else {
      return this.addPrefixDate(replDate, prefix);
    }
  }

  /**
   * [一般開発者利用不可]
   * 日付を変換
   * @param dateString 日付値が入力されます
   * @param isJP JPの日付形式
   * @param start 開始位置は値を取得します
   * @param end 終了位置は値を取得します
   * @param prefix 文字をフォーマットする
   * @returns 日付を変換
   */
  private convertDateSlice(dateString: string, isJP: boolean, start: number, end: number, prefix?: string) :string {
    const replDate = this.stringValid(dateString);
    if (SystemConstant.EMPTY === replDate) {
      return dateString;
    }

    if (isJP) {
      return this.addPrefixDateJP(replDate).slice(start, end);
    } else {
      return this.addPrefixDate(replDate, prefix).slice(start, end);
    }
  }

  /**
   * [一般開発者利用不可]
   * 無効な形式を無視する
   * @param inputDateFormat 入力日をフォーマットする「YYYYMMDD,　YYYY-MM-DD, YYYY/MM/DD, YYYYMM, YYYY-MM, YYYY/MM」
   * @param outputDateFormat 出力日をフォーマットします「YYYYMMDD,　YYYY-MM-DD, YYYY/MM/DD, YYYY年MM月DD日
   *     YYMMDD, YY-MM-DD, YY/MM/DD, YY年MM月DD日, YYYYMM, YYYY-MM, YYYY/MM, YYYY年MM月」
   * @returns outputDateFormatは日付が存在するが、inputDateFormatは日付が存在しない場合
   */
  private skipDataInvalid(inputDateFormat: string, outputDateFormat: string) : boolean {
    if ((inputDateFormat === 'YYYYMMDD' || inputDateFormat ==='YYYY-MM-DD' || inputDateFormat ==='YYYY/MM/DD')
        && (outputDateFormat === 'YYYYMMDD' || outputDateFormat === 'YYYY-MM-DD' || outputDateFormat === 'YYYY/MM/DD'
            || outputDateFormat === 'YYYY年MM月DD日' || outputDateFormat === 'YYMMDD' || outputDateFormat === 'YY-MM-DD'
            || outputDateFormat === 'YY/MM/DD' || outputDateFormat === 'YY年MM月DD日')) {
          return true;
    }

    if (outputDateFormat === 'YYYYMM' || outputDateFormat === 'YYYY-MM' || outputDateFormat === 'YYYY/MM' || outputDateFormat === 'YYYY年MM月') {
          return true;
    }
    return false;
  }
}


